/**
 * Gmail / Google Workspace email-log seam.
 *
 * Sibling to `meetNotes.ts`: where that pulls Google Meet notes, this
 * pulls recent Gmail threads for a contact / account so the email trail
 * (account `EmailEntry[]`) and the contact activity timeline become
 * *effectively automatic* rather than hand-entered. WHY this matters:
 * an activity timeline is only trustworthy when it captures touches on
 * its own — manual logging always drifts incomplete (UX_REVIEW move #4,
 * "the timeline is only trustworthy when it's effectively automatic").
 *
 * Same env-flag contract as `meetNotes.ts` — Gmail lives behind the same
 * hosted Workspace worker (`VITE_GSUITE_API_URL`), which owns the OAuth
 * and the Gmail API calls. When the URL is NOT set (local dev, demos)
 * `isConfigured()` is false and `pullEmailThreads()` falls back to a
 * *deterministic local simulation* so "Sync Gmail" is fully usable
 * offline. No secrets ever touch the client.
 *
 *   POST {base}/email-log  { name?, company?, accountName?, email? }
 *                          → { messages: EmailMessage[] }
 */

import type { EmailEntry } from '../db/types';

const BASE_URL: string | undefined = (
  import.meta.env.VITE_GSUITE_API_URL as string | undefined
)?.replace(/\/+$/, '');
const API_KEY = import.meta.env.VITE_GSUITE_API_KEY as string | undefined;

const TIMEOUT_MS = 30_000;

/** True when a real Google Workspace endpoint is configured. */
export function isConfigured(): boolean {
  return Boolean(BASE_URL);
}

/** Backend descriptor for status pills / debugging. */
export function emailInfo(): { configured: boolean; baseUrl: string | null } {
  return { configured: isConfigured(), baseUrl: BASE_URL ?? null };
}

/** Who/what we're pulling email for (mirrors `MeetContext`). */
export interface EmailContext {
  /** Contact / person name, when syncing from a contact card. */
  name?: string;
  /** Company on the contact, used to colour the simulated thread. */
  company?: string;
  /** Account name, when syncing from an account detail view. */
  accountName?: string;
  /** Contact email — helps the real worker match the Gmail thread. */
  email?: string;
}

/**
 * One Gmail message mapped to the persisted shapes. The field set is a
 * superset of `EmailEntry` (account trail) plus `source`; `toEmailEntry`
 * narrows it for the account path and `formatEmailActivity` renders it
 * for the contact timeline.
 */
export interface EmailMessage {
  id: string;
  /** ISO-8601 UTC. */
  at: string;
  /** `in` = received from the counterpart, `out` = sent by us. */
  direction: 'in' | 'out';
  subject: string;
  /** "Name <email>" of the sender. */
  from?: string;
  /** "Name <email>" of the recipient. */
  to?: string;
  /** One-line snippet of the body. */
  preview?: string;
  /** Always "Gmail" today. */
  source: string;
}

export class EmailLogError extends Error {
  constructor(message: string, readonly cause?: unknown) {
    super(message);
    this.name = 'EmailLogError';
  }
}

/**
 * Pull recent Gmail threads for a contact / account. Never throws — a
 * worker/network failure downgrades to the local simulation so the
 * action always produces messages.
 */
export async function pullEmailThreads(context: EmailContext): Promise<EmailMessage[]> {
  if (isConfigured()) {
    try {
      return await postEmailLog(context);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[Argo gsuite] email worker failed, using local simulation:', err);
    }
  }
  return simulateEmailThreads(context);
}

/**
 * Narrow an `EmailMessage` to the account-trail `EmailEntry` shape
 * (drops `source`/`id`; `appendEmail` assigns the id). Used by the
 * account "Sync Gmail" write path.
 */
export function toEmailEntry(message: EmailMessage): Omit<EmailEntry, 'id'> {
  return {
    at: message.at,
    direction: message.direction,
    subject: message.subject,
    from: message.from,
    to: message.to,
    preview: message.preview,
  };
}

/**
 * Format a Gmail message as a single contact-timeline line. Used by the
 * contact "Sync email" path (`logPersonActivity` with `email` kind) so
 * synced mail reads consistently alongside hand-logged touches.
 */
export function formatEmailActivity(message: EmailMessage): string {
  const when = new Date(message.at).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
  const arrow = message.direction === 'in' ? '←' : '→';
  const body = message.preview ? ` — ${message.preview}` : '';
  return `${arrow} ${message.subject} (${when})${body}`;
}

/**
 * Stable de-dupe key so repeated syncs are idempotent. Keyed on
 * direction + subject + calendar day (the simulation is deterministic
 * per context, so re-syncing would otherwise duplicate every message).
 */
export function emailDedupeKey(m: { direction: 'in' | 'out'; subject: string; at: string }): string {
  return `${m.direction}|${m.subject.trim().toLowerCase()}|${m.at.slice(0, 10)}`;
}

async function postEmailLog(context: EmailContext): Promise<EmailMessage[]> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
  try {
    const res = await fetch(`${BASE_URL}/email-log`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(API_KEY ? { Authorization: `Bearer ${API_KEY}` } : {}),
      },
      body: JSON.stringify(context),
      signal: controller.signal,
    });
    if (!res.ok) {
      throw new EmailLogError(
        `GSuite worker responded ${res.status} ${res.statusText}`,
      );
    }
    const raw = (await res.json()) as { messages?: unknown };
    const messages = Array.isArray(raw.messages) ? raw.messages : [];
    return messages
      .map(coerceMessage)
      .filter((m): m is EmailMessage => m !== null);
  } finally {
    clearTimeout(timer);
  }
}

/** Defensively coerce an untrusted worker payload into an EmailMessage. */
function coerceMessage(value: unknown): EmailMessage | null {
  if (!value || typeof value !== 'object') return null;
  const v = value as Record<string, unknown>;
  const subject = typeof v.subject === 'string' ? v.subject : undefined;
  if (!subject) return null;
  const direction = v.direction === 'in' ? 'in' : 'out';
  return {
    id: typeof v.id === 'string' ? v.id : crypto.randomUUID(),
    at: typeof v.at === 'string' ? v.at : new Date().toISOString(),
    direction,
    subject,
    from: typeof v.from === 'string' ? v.from : undefined,
    to: typeof v.to === 'string' ? v.to : undefined,
    preview: typeof v.preview === 'string' ? v.preview : undefined,
    source: typeof v.source === 'string' ? v.source : 'Gmail',
  };
}

// ---------------------------------------------------------------------------
// Deterministic local simulation
// ---------------------------------------------------------------------------

/** Stable 0–1 hash so the same context always reads the same way. */
function hash01(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return ((h >>> 0) % 1000) / 1000;
}

/**
 * A believable two-to-three-message thread, alternating in/out so the
 * trail reads like a real back-and-forth. Each entry is relative to a
 * "days ago" anchor so the most recent message is freshest.
 */
const THREAD_TEMPLATES: {
  subject: string;
  steps: { direction: 'in' | 'out'; preview: string; daysAgo: number }[];
}[] = [
  {
    subject: 'Intro — Argo automation for {subject}',
    steps: [
      { direction: 'out', preview: 'Quick note on how we could remove the manual steps in your workflow. Worth 20 minutes?', daysAgo: 12 },
      { direction: 'in', preview: 'Thanks — this is timely. Tuesday or Wednesday afternoon works for an intro call.', daysAgo: 11 },
    ],
  },
  {
    subject: 'Re: Discovery follow-up & ROI estimate',
    steps: [
      { direction: 'out', preview: 'Recap + the tailored ROI estimate we discussed. Happy to walk the eng team through the integration surface.', daysAgo: 6 },
      { direction: 'in', preview: 'Numbers look strong. Looping in our eng lead on security/SLA before we scope a pilot.', daysAgo: 5 },
      { direction: 'out', preview: 'Attached the security one-pager. Proposing a 4-week pilot — does that timeline work?', daysAgo: 4 },
    ],
  },
  {
    subject: 'Proposal & pilot scope',
    steps: [
      { direction: 'out', preview: 'Proposal attached. Pricing is annual with a short pilot up front, as requested.', daysAgo: 3 },
      { direction: 'in', preview: 'Reviewing internally — should have sign-off by end of week. Two small questions on success metrics.', daysAgo: 2 },
    ],
  },
  {
    subject: 'Quarterly check-in & expansion',
    steps: [
      { direction: 'in', preview: 'Adoption is up across the team. Two adjacent groups are asking about access.', daysAgo: 8 },
      { direction: 'out', preview: 'Great to hear — happy to prep an expansion deck and intro the adjacent leads.', daysAgo: 7 },
    ],
  },
];

const ARGO_SENDER = 'Trent Hughes <trent@argoiq.com>';

function simulateEmailThreads(context: EmailContext): EmailMessage[] {
  const subject = context.accountName ?? context.company ?? context.name ?? 'your team';
  const counterpartName = context.name ?? `${subject} team`;
  const counterpart = context.email
    ? `${counterpartName} <${context.email}>`
    : counterpartName;
  const seed = `${subject}|${context.name ?? ''}|${context.email ?? ''}`;
  const r = hash01(seed);

  // 1–2 threads, deterministic from the hash.
  const threadCount = 1 + (Math.floor(r * 100) % 2);
  const startIdx = Math.floor(r * THREAD_TEMPLATES.length);
  const messages: EmailMessage[] = [];

  for (let t = 0; t < threadCount; t++) {
    const tmpl = THREAD_TEMPLATES[(startIdx + t) % THREAD_TEMPLATES.length];
    const subjectLine = tmpl.subject.replace('{subject}', subject);
    for (const step of tmpl.steps) {
      const at = new Date(
        Date.now() - step.daysAgo * 24 * 60 * 60 * 1000,
      ).toISOString();
      messages.push({
        id: crypto.randomUUID(),
        at,
        direction: step.direction,
        subject: subjectLine,
        from: step.direction === 'in' ? counterpart : ARGO_SENDER,
        to: step.direction === 'in' ? ARGO_SENDER : counterpart,
        preview: step.preview,
        source: 'Gmail',
      });
    }
  }

  // Most-recent-first for display / append ordering by the caller.
  messages.sort((a, b) => b.at.localeCompare(a.at));
  return messages;
}
